import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  MonetizationOn as MonetizationOnIcon,
  Phone as PhoneIcon,
  Description as ProposalIcon,
  Search as SearchIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";
import { useCallback } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchAndSort } from "../../hooks/useSearchAndSort";
import OTPVerification from "../../components/borrowers/OTPVerification";
import DataTable from "../../components/common/DataTable";
import LenderOnboardingPopup from "../../components/lenders/LenderOnboardingPopup";
import BorrowerTypeSelectModal from "../../components/users/BorrowerTypeSelectModal";
import UserForm from "../../components/users/UserForm";
import {
  commonColumns,
  userManagementColumns,
} from "../../constants/tableColumns";
import {
  createBorrower,
  createUser,
  deleteBorrower,
  deleteLender,
  deleteConsultantById,
  deleteUser,
  fetchBorrowerByUuid,
  fetchBorrowers,
  fetchLenders,
  fetchUsers,
  fetchConsultants,
  getAllRoles,
  getConsultantById,
  getLenderById,
  getUserByUuid,
  updateBorrower,
  updateLender,
  updateUser,
  verifyLender,
  createConsultant,
} from "../../services/userService";
import { coordinates, ip } from "../../Store/userSlice";
import { toast } from "react-toastify";

const UserManagement = ({ userType = "manager" }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [mode, setMode] = useState("add");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [roles, setRoles] = useState([]);

  const isLoadingRef = useRef(false);
  const rolesRef = useRef(roles);
  const loadUsersRef = useRef();
  const loadUsersAndRolesRef = useRef();
  const initialLoadRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    rolesRef.current = roles;
  }, [roles]);

  useEffect(() => {
    const { openAddBorrower } = location.state ?? {};
    if (userType !== "borrower" || !openAddBorrower) return;
    setSelectedUserType("individual");
    setMode("add");
    setOpen(true);
    navigate("/users/borrowers", { replace: true, state: {} });
  }, [userType, location.state, navigate]);

  // Set a title based on userType
  const pageTitle =
    userType === "manager"
      ? "Manage Managers"
      : userType === "consultant"
      ? "Manage Consultants"
      : userType === "borrower"
      ? "Manage Borrowers"
      : "Manage Lenders";

  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [borrowerRequestData] = useState(null);
  const [showMobileOTP] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedLender, setSelectedLender] = useState(null);
  const [verifyUserId, setVerifyUserId] = useState("");
  const [verifyLenderId, setVerifyLenderId] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [lenderOnboardingOpen, setLenderOnboardingOpen] = useState(false);
  const {searchQuery, setSearchQuery, handleSort: baseHandleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, userType);

  const handleSort = useCallback((columnId, direction) => {
    initialLoadRef.current = false;
    baseHandleSort(columnId, direction);
  }, [baseHandleSort]);

  const IpAddress = useSelector(ip);
  const userCoordinates = useSelector(coordinates);
  const userPermission = useSelector((state) => state.user.userPermissions);
  const [permission, setPermissions] = useState({});
  useEffect(() => {
    console.log("User Permissions:", userPermission);
    const moduleList = userPermission?.map((e) => e?.module);
    if (moduleList && moduleList.length > 0) {
      const hasLeadsPermission =
        moduleList.includes("managers") ||
        moduleList.includes("borrowers") ||
        moduleList.includes("lenders");
      if (!hasLeadsPermission) {
        //   showToast("You do not have permission to access Leads", "error");
        navigate("/dashboard");
      } else {
        console.log(
          "User Permissions for Leads:",
          userPermission.find(
            (e) => e.module === "leads"
          )
        );
        setPermissions(
          JSON.parse(
            userPermission.find((e) => e.module === "leads")?.permission
          ) || null
        );
      }
    }
  }, [userPermission, navigate]);

  const formatBorrowerType = useCallback((borrowerType, entityType, entityName) => {
    if (!borrowerType) return "N/A";

    const typeMap = {
      individual: "Individual",
      entity: (type, name) => {
        const entityTypes = {
          company: "Company",
          partnership_firm: "Partnership",
          propreitorship_firm: "Proprietorship",
        };
        const displayType =
          entityTypes[type] ||
          type?.charAt(0).toUpperCase() + type?.slice(1) ||
          "Entity";
        return name ? `${displayType} | ${name}` : displayType;
      },
    };

    return borrowerType === "entity"
      ? typeMap.entity(entityType, entityName)
      : typeMap[borrowerType] || "N/A";
  }, []);

  const FIELD_MAP = Object.freeze(new Map([
    ['manager', Object.freeze(new Map([['role', 'roleId']]))],
    ['consultant', Object.freeze(new Map([['phoneNumber', 'mobileNumber']]))],
    ['borrower', Object.freeze(new Map([
      ['phoneNumber', 'mobileNumber'],
      ['businessType', 'entityType'],
      ['taggedLocation', 'address']
    ]))],
    ['lender', Object.freeze(new Map([
      ['uuid', 'lenderId'],
      ['phoneNumber', 'mobileNumber'],
      ['financierType', 'financialInstitutionType'],
      ['taggedLocation', 'address'],
      ['verificationStatus', 'status']
    ]))]
  ]));

  const getSortByField = useCallback((userType, columnId) => 
    FIELD_MAP.get(userType)?.get(columnId) ?? columnId, [FIELD_MAP]);

  const loadUsers = useCallback(async (currentRoles) => {
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const loggeinUserRoleId =
        (currentRoles || rolesRef.current)?.find((role) => role.role === userData.role)?.roleId || 1;

      const apiParams = {
        page, 
        size: rowsPerPage, 
        ...searchAndSortParams,
        ...(searchAndSortParams.sortBy && { 
          sortBy: getSortByField(userType, searchAndSortParams.sortBy) 
        })
      };

      const fetchStrategies = new Map([
        ['borrower', () => fetchBorrowers(apiParams)],
        ['lender', () => fetchLenders(apiParams)],
        ['manager', () => fetchUsers({ roleId: loggeinUserRoleId, ...apiParams })],
        ['consultant', () => fetchConsultants({ userType: "consultant", ...apiParams })],
      ]);

      const response = await fetchStrategies.get(userType)?.();
      const resultData = response?.result?.result ?? response?.result;
      const dataArray = userType === "lender"
        ? (response?.result?.lenders ?? response?.lenders ?? [])
        : (Array.isArray(resultData?.content) 
            ? resultData.content 
            : Array.isArray(resultData) 
              ? resultData 
              : []);

      const validUserTypes = new Set(['manager', 'consultant', 'borrower', 'lender']);
      if (validUserTypes.has(userType)) {
        setTotalCount(
          response?.rowCount ??
          response?.result?.rowCount ??
          resultData?.totalElements ??
          response?.totalElements ??
          dataArray?.length ??
          0
        );
      }

      if (!dataArray?.length) {
        console.warn(
          `Empty or unexpected ${userType} API response format:`, 
          response
        );
        setUsers([]);
        return [];
      }

      const mapBorrowerData = (borrower) => ({
        id: borrower.id,
        uuid: borrower.uuid,
        name: borrower.name,
        email: borrower.email,
        role: "Borrower",
        userType: borrower.userType,
        borrowerType: borrower.borrowerType,
        entityType: borrower.entityType,
        entityName: borrower.entityName,
        displayType: formatBorrowerType(
          borrower.borrowerType, 
          borrower.entityType, 
          borrower.entityName
        ),
        businessType: formatBorrowerType(
          borrower.borrowerType, 
          borrower.entityType, 
          borrower.entityName
        ),
        status: borrower.resultSet === 1 ? "Active" : "Inactive",
        phoneNumber: borrower.mobileNumber ?? borrower.phoneNumber ?? "",
        taggedLocation: borrower.address ?? "",
        gstNumber: borrower.gstNumber ?? "",
        address: borrower.address ?? "",
        profilePhotoUrl: borrower.profilePhotoUrl ?? null,
        ip: borrower.ip ?? "",
        latitude: borrower.latitude ?? "",
        longitude: borrower.longitude ?? "",
      });

      const mapLenderData = (lender) => ({
        id: lender.id,
        name: lender.name || "N/A",
        email: lender.email || "N/A",
        phoneNumber: lender.mobileNumber || lender.phoneNumber || "N/A",
        uuid: lender.lender_lenderId,
        role: "Lender",
        financierType: getFinancierTypeLabel(
          lender.lender_financialInstitutionType
        ),
        typeOfLending: getTypeOfLendingLabel(lender.lender_typeOfLending),
        typeOfRetailLoans: getLendingTypeLabel(lender.lender_typeOfRetailLoans),
        designation: lender.lender_designation || "N/A",
        taggedLocation: lender.lender_location || "N/A",
        verificationStatus: lender.lender_verified ? "Verified" : "Pending",
        rawFinancierType: lender.lender_financialInstitutionType,
        rawTypeOfLending: lender.lender_typeOfLending,
        rawTypeOfRetailLoans: lender.lender_typeOfRetailLoans,
        verified: lender.lender_verified,
        status: lender.status,
        createDatetime: lender.createDatetime,
        commitDatetime: lender.commitDatetime,
        createOpName: lender.createOpName,
        commitOpName: lender.commitOpName,
        userType: lender.userType,
        borrowerType: lender.borrowerType,
        gstNumber: lender.gstNumber,
        address: lender.address,
        profilePhotoUrl: lender.profilePhotoUrl,
        ip: lender.ip,
        latitude: lender.latitude,
        longitude: lender.longitude,
      });

      const dataMappers = new Map([
        ['borrower', () => dataArray.map(mapBorrowerData)],
        ['lender', () => dataArray.map(mapLenderData)],
        ['consultant', () => dataArray.map(c => ({
          id: c.id,
          uuid: c.uuid,
          name: c.name,
          email: c.email,
          phoneNumber: c.mobileNumber,
          role: "Consultant",
          status: c.resultSet === 1 || c.status === "active" ? "Active" : "Inactive",
          companyName: c.companyName ?? "",
          professionalWebsite: c.professionalWebsite ?? "",
          yearsOfExperience: c.yearsOfExperience ?? "",
          loanSpecialization: c.loanSpecialization ?? [],
        }))],
        ['manager', () => dataArray.map(user => {
          const userRole = (currentRoles || rolesRef.current).find(role => role.roleId === user.roleId);
          return {
            ...user,
            role: user.roleId === 1 ? "Superadmin" : userRole?.role ?? user.role ?? "N/A",
          };
        })]
      ]);

      const usersData = dataMappers.get(userType)?.() ?? [];

      setUsers(usersData);
      return usersData;
    } catch (error) {
      console.error("❌ Error loading users:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to load users",
        severity: "error",
      });
      return [];
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userType, searchAndSortParams, getSortByField, formatBorrowerType, page, rowsPerPage]);

  const refreshUsersList = useCallback(async () => {
    isLoadingRef.current = false;
    await loadUsers();
  }, [loadUsers]);

  const loadUsersAndRoles = useCallback(async () => {
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      
      if (userType === "manager") {
        const rolesData = await getAllRoles();
        setRoles(rolesData || []);
        rolesRef.current = rolesData || [];
      } else {
        setRoles([]);
        rolesRef.current = [];
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to load initial data",
        severity: "error",
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userType]);

  useEffect(() => {
    loadUsersRef.current = loadUsers;
    loadUsersAndRolesRef.current = loadUsersAndRoles;
  }, [loadUsers, loadUsersAndRoles]);

  useEffect(() => {
    setSearchQuery("");
    initialLoadRef.current = true;
  }, [userType, setSearchQuery]);

  useEffect(() => {
    if (isLoadingRef.current) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setSnackbar({
        open: true,
        message: "Authentication required. Please log in again.",
        severity: "error",
      });
      return;
    }

    if (loadUsersAndRolesRef.current) {
      loadUsersAndRolesRef.current();
    }

    return () => {
      isLoadingRef.current = false;
    };
  }, [userType]);

  useEffect(() => {
    if (userType === "manager" && roles.length > 0 && !isLoadingRef.current) {
      if (loadUsersRef.current) {
        loadUsersRef.current(roles);
      }
    }
  }, [roles, userType]);

  useEffect(() => {
    if (userType !== "manager" && userType !== "consultant" && userType !== "borrower" && userType !== "lender") return;
    if (isLoadingRef.current) return;
    
    if (userType === "manager") {
      if (roles.length > 0) {
        if (loadUsersRef.current) {
          loadUsersRef.current(roles);
        }
      }
    } else {
      if (loadUsersRef.current) {
        loadUsersRef.current();
      }
    }
  }, [page, rowsPerPage, userType, roles]);

  useEffect(() => {
    if (!["manager", "consultant", "borrower", "lender"].includes(userType)) return;
    if (isLoadingRef.current) return;
    
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    
    if (userType === "manager") {
      if (roles.length > 0) {
        if (loadUsersRef.current) {
          loadUsersRef.current(roles);
        }
      }
    } else {
      if (loadUsersRef.current) {
        loadUsersRef.current();
      }
    }
  }, [searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order, userType, roles]);

  const normalizeYearsForSubmit = (value) => (value ? String(value).replace(/Years/i, 'years') : "");

  const buildConsultantPayload = (formData) => ({
    name: formData.name,
    email: formData.email,
    mobileNumber: formData.phoneNumber,
    companyName: formData.companyName || "",
    professionalWebsite: formData.website || "",
    yearsOfExperience: normalizeYearsForSubmit(formData.yearsExperience),
    loanSpecialization: Array.isArray(formData.primarySpecializations) ? formData.primarySpecializations : [],
    otherSpecialization: formData.specializationOther || "",
    userType: "consultant",
  });

  const submitConsultant = async (formData, successMessage) => {
    const response = await createConsultant(buildConsultantPayload(formData));
    if (response && (response.statusCode === 200 || response.responseCode === 200)) {
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: (response.message || response.responseMessage) || successMessage,
        severity: "success",
      });
      await refreshUsersList();
      return true;
    }
    setSnackbar({
      open: true,
      message: (response?.message || response?.responseMessage) || "Failed to process consultant",
      severity: "error",
    });
    return false;
  };

  const getColumns = useCallback(() => {
    const columnDefs = {
      common: [
        commonColumns.name,
        commonColumns.email,
        commonColumns.phoneNumber,
        (permission.edit === 1 ||
          permission?.delete === 1 ||
          permission?.view === 1) &&
          commonColumns.actions,
      ],
      manager: userManagementColumns.manager,
      consultant: userManagementColumns.consultant,
      lender: userManagementColumns.lender,
      borrower: userManagementColumns.borrower,
    };

    let columns = [];

    if (userType === "manager") {
      columns = [
        columnDefs.manager[0],
        ...columnDefs.common.slice(0, -1),
        ...columnDefs.manager.slice(1),
        columnDefs.common[3],
      ];
    } else if (userType === "consultant") {
      columns = [...columnDefs.consultant, columnDefs.common[3]];
    } else if (userType === "lender") {
      columns = [...columnDefs.lender, columnDefs.common[3]];
    } else {
      columns = [
        ...columnDefs.common.slice(0, -1),
        ...columnDefs.borrower,
        columnDefs.common[3],
      ];
    }

    return columns;
  }, [permission, userType]);
  
  const columns = useMemo(() => getColumns(), [getColumns]);

  const getLendingTypeLabel = (lendingType) => {
    switch (lendingType) {
      // Borrower types
      case "INDIVIDUAL":
      case "individual":
        return "Individual";
      case "ENTITY":
      case "entity":
        return "Entity";

      // Entity subtypes (for display purposes)
      case "COMPANY":
        return "Entity - Company";
      case "PARTNERSHIP":
        return "Entity - Partnership";
      case "PROPRIETORSHIP":
        return "Entity - Proprietorship";

      // Lender types for managers
      case "PERSONAL":
        return "Personal Loan";
      case "BUSINESS":
        return "Business Loan";
      case "HOME":
        return "Home Loan";
      case "VEHICLE":
        return "Vehicle Loan";
      case "EDUCATION":
        return "Education Loan";

      case "":
        return "None";
      default:
        return lendingType || "N/A";
    }
  };

  const getFinancierTypeLabel = (financierType) => {
    switch (financierType) {
      case "BANK":
        return "Bank";
      case "NBFC":
        return "NBFC";
      case "CREDIT_UNION":
        return "Credit Union";
      case "PRIVATE":
        return "Private Lender";
      case "GOVERNMENT":
        return "Government";
      default:
        return financierType || "N/A";
    }
  };

  const getTypeOfLendingLabel = (type) => {
    if (!type) return "N/A";

    const lendingType = type.toLowerCase();
    switch (lendingType) {
      case "secured":
        return "Secured";
      case "unsecured":
        return "Unsecured";
      case "both":
        return "Both";
      default:
        return type;
    }
  };

  const handleCommonApiErrors = (message, setErrorState = false) => {
    if (!message) return false;

    const lowerMsg = message.toLowerCase();

    if (
      lowerMsg.includes("email already exists") ||
      lowerMsg.includes("email is already used") ||
      lowerMsg.includes("email is already taken") ||
      message === "Email is already used for another account."
    ) {
      setSnackbar({
        open: true,
        message: "Email already exists. Please use a different email address.",
        severity: "error",
      });
      return true;
    }

    if (
      lowerMsg.includes("unique_username") ||
      (lowerMsg.includes("username") && lowerMsg.includes("already exists")) ||
      lowerMsg.includes("username is already taken") ||
      message === "Username is already taken."
    ) {
      if (setErrorState) {
        setError(
          "Username already exists. Please choose a different username."
        );
      }

      setSnackbar({
        open: true,
        message: "Username already exists. Please choose a different username.",
        severity: "error",
      });
      return true;
    }

    if (
      lowerMsg.includes("phone number is already used") ||
      lowerMsg.includes("phone number already exists") ||
      message === "Phone number is already used for another account" ||
      message === "Phone number is already used for another account."
    ) {
      setSnackbar({
        open: true,
        message:
          "Phone number already exists. Please use a different phone number.",
        severity: "error",
      });
      return true;
    }

    return false;
  };

  const handleOpenTypeSelectDialog = () => {
    // Skip type selection for non-managers
    if (userType !== "manager" && userType !== "consultant") {
      setSelectedUserType(userType === "borrower" ? "individual" : userType);
      setMode("add");
      setOpen(true);
      return;
    }

    handleSelectUserType("manager");
  };

  const handleCloseTypeSelectDialog = () => {
    setTypeSelectOpen(false);
    setSelectedUserType(null);
  };

  const handleSelectUserType = (selectedType) => {
    setSelectedUserType(selectedType);
    setTypeSelectOpen(false);
    setMode("add");
    setOpen(true);
  };

  const isBorrower = useCallback((user) => {
    if (!user) return false;
    return user.uuid?.startsWith("temp-") || user.role === "Borrower";
  }, []);

  const handleOpenDialog = useCallback(
    async (user = null) => {
      setLoading(true);

      try {
        if (!user) {
          setMode("add");
          setOpen(true);
          setLoading(false);
          return;
        }

        const fetchUserDetails = {
          lender: async () => {
            const details = await getLenderById(user.uuid);
            if (!details?.result?.lender)
              throw new Error("Invalid lender details");

            const lenderData = details.result.lender;
            return {
              id: lenderData.id,
              uuid: lenderData.lender_lenderId,
              name: lenderData.name || "",
              financialInstitution: lenderData.lender_financialInstitution || "",
              email: lenderData.email || "",
              phoneNumber: lenderData.mobileNumber,
              financialInstitutionType: (
                lenderData.lender_financialInstitutionType || ""
              ).toUpperCase(),
              typeOfLending: (
                lenderData.lender_typeOfLending || ""
              ).toUpperCase(),
              typeOfRetailLoans: (
                lenderData.lender_typeOfRetailLoans || ""
              ).toUpperCase(),
              designation: lenderData.lender_designation,
              location: lenderData.lender_location,
              verified: lenderData.lender_verified,
              status: lenderData.status,
            };
          },
          borrower: async () => {
            const details = await fetchBorrowerByUuid(user.uuid);
            const borrowerData = Array.isArray(details?.result)
              ? details.result.find((b) => b.uuid === user.uuid)
              : details?.result;

            if (!borrowerData) {
              // Fall back to using the passed user data
              return {
                ...user,
                userType: "borrower",
                borrowerType: user.borrowerType || "individual",
                entityType: user.entityType || "",
                entityName: user.entityName || "",
                gstNumber: user.gstNumber || "",
                address: user.address || "",
                profilePhotoUrl: user.profilePhotoUrl || null,
                mobileNumber: user.phoneNumber || user.mobileNumber,
                phoneNumber: user.phoneNumber || user.mobileNumber,
              };
            }

            return {
              id: borrowerData.id,
              uuid: borrowerData.uuid,
              name: borrowerData.name,
              email: borrowerData.email,
              role: "Borrower",
              userType: borrowerData.userType || "borrower",
              borrowerType: borrowerData.borrowerType || "individual",
              entityType: borrowerData.entityType || "",
              entityName: borrowerData.entityName || "",
              mobileNumber: borrowerData.mobileNumber,
              phoneNumber: borrowerData.mobileNumber,
              gstNumber: borrowerData.gstNumber || "",
              address: borrowerData.address || "",
              profilePhotoUrl: borrowerData.profilePhotoUrl || null,
              ip: borrowerData.ip || "",
              latitude: borrowerData.latitude || "",
              longitude: borrowerData.longitude || "",
            };
          },
          manager: async () => {
            try {
              const details = await getUserByUuid(user.uuid);
              const managerData = Array.isArray(details?.result)
                ? details.result.find((m) => m.uuid === user.uuid)
                : details?.result;

              if (!managerData) {
                return {
                  ...user,
                  userType: user.userType || user.rawLendingType,
                  entityType: user.entityType || user.rawEntityType,
                  financierType: user.financierType || user.rawFinancierType,
                };
              }

              return {
                id: managerData.id,
                uuid: managerData.uuid,
                username: managerData.username,
                name: managerData.name,
                email: managerData.email,
                roleId: managerData.roleId,
                phoneNumber: managerData.phoneNumber,
                region: managerData.region || "",
                area: managerData.area || "",
                userType: managerData.lendingType || "",
                entityType: managerData.entityType || "",
                financierType: managerData.financierType || "",
                ip: managerData.ip || "",
                latitude: managerData.latitude || "",
                longitude: managerData.longitude || "",
              };
            } catch (error) {
              console.error("Error fetching manager details:", error);
              return {
                ...user,
                userType: user.userType || user.rawLendingType,
                entityType: user.entityType || user.rawEntityType,
                financierType: user.financierType || user.rawFinancierType,
              };
            }
          },
          consultant: async () => {
            try {
              const res = await getConsultantById(user.id);
              const consultant = res?.result?.result || res?.result || res;
              if (!consultant) throw new Error("Invalid consultant details");
              
              const normalizeYears = (val) => {
                if (!val) return "";
                const value = String(val).toLowerCase();
                const yearMap = {
                  "10+": "10+ Years",
                  "5-10": "5-10 Years", 
                  "3-5": "3-5 Years",
                  "1-3": "1-3 Years"
                };
                return Object.entries(yearMap).find(([key]) => value.includes(key))?.[1] || val;
              };

              return {
                id: consultant.id,
                uuid: consultant.uuid,
                name: consultant.name ?? "",
                email: consultant.email ?? "",
                phoneNumber: consultant.mobileNumber ?? "",
                companyName: consultant.companyName ?? "",
                website: consultant.professionalWebsite ?? "",
                yearsExperience: normalizeYears(consultant.yearsOfExperience),
                primarySpecializations: Array.isArray(consultant.loanSpecialization) ? consultant.loanSpecialization : [],
                specializationOther: consultant.otherSpecialization ?? "",
                status: consultant.status,
              };
            } catch (error) {
              toast.error("Error fetching consultant details:", error);
              return { ...user };
            }
          },
        };

        const fetchFunction = isBorrower(user)
          ? fetchUserDetails.borrower
          : fetchUserDetails[userType];
      if (!fetchFunction) {
          throw new Error(`Invalid user type: ${userType}`);
        }

        const userDetails = await fetchFunction();
        setSelectedUser(userDetails);
        setMode("edit");
        setOpen(true);
      } catch (error) {
        console.error("Error fetching user details:", error);
        setError("Failed to fetch user details. Please try again.");
        // Still open the dialog with the original user data as fallback
        setSelectedUser(user);
        setMode("edit");
        setOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [
      getLenderById,
      fetchBorrowerByUuid,
      getUserByUuid,
      isBorrower,
      userType,
    ]
  );

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedUser(null);
    setMode("add");
    // Reset the selectedUserType as well
    setSelectedUserType(null);
    // Reset any error states
    setError(null);
  };

  const handleAddUser = async (data) => {
    if (isLoadingRef.current || loading) return;
    try {
      isLoadingRef.current = true;
      setLoading(true);

      // For manager, check username capitalization
      if (
        selectedUserType === "manager" &&
        data.username &&
        /^[A-Z]/.test(data.username)
      ) {
        setSnackbar({
          open: true,
          message: "Username should not start with a capital letter",
          severity: "error",
        });
        setLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // If adding a borrower, use the borrower API
      if (userType === "borrower") {
        // Map form fields to API fields
        const borrowerData = {
          name: data.name,
          mobileNumber: data.phoneNumber,
          email: data.email,
          address: data.address || "",
          gstNumber: data.gstNumber || "",
          file: data.file,
          ip: IpAddress,
          longitude: userCoordinates?.lng || "",
          latitude: userCoordinates?.lat || "",
          userType: "borrower",
          borrowerType: data.userType === "entity" ? "entity" : "individual",
        };

        // Only add entity-related fields if it's an entity borrower
        if (data.userType === "entity") {
          borrowerData.entityType = data.entityType;
          borrowerData.entityName = data.entityName || "";
          const validEntityTypes = [
            "company",
            "partnership_firm",
            "propreitorship_firm",
          ];
          if (!validEntityTypes.includes(borrowerData.entityType)) {
            setSnackbar({
              open: true,
              message:
                "Invalid entity type. Must be one of: company, partnership_firm, propreitorship_firm",
              severity: "error",
            });
            setLoading(false);
            isLoadingRef.current = false;
            return;
          }
        }

        try {
          const response = await createBorrower(borrowerData);

          if (
            response?.responseMessage
              ?.toLowerCase()
              .includes("phone number is already used") ||
            response?.responseMessage
              ?.toLowerCase()
              .includes("email is already used")
          ) {
            setSnackbar({
              open: true,
              message: response.responseMessage,
              severity: "error",
            });
            return;
          }

          if (
            response &&
            response.responseCode === 200 &&
            response.responseMessage &&
            (response.responseMessage.toLowerCase().includes("success") ||
              response.responseMessage.toLowerCase().includes("created"))
          ) {
            handleCloseDialog();
            setSnackbar({
              open: true,
              message:
                response.responseMessage || "Borrower added successfully!",
              severity: "success",
            });

            await refreshUsersList();
            setSelectedUserType(null);
          } else if (
            response &&
            response.responseCode === 200 &&
            handleCommonApiErrors(response.responseMessage)
          ) {
            // Error already handled by handleCommonApiErrors
            return;
          } else if (response) {
            const errorMsg = response.responseMessage || "";

            if (
              errorMsg.toLowerCase().includes("success") ||
              errorMsg.toLowerCase().includes("created")
            ) {
              handleCloseDialog();
              setSnackbar({
                open: true,
                message: errorMsg || "Borrower added successfully!",
                severity: "success",
              });

              await refreshUsersList();
              setSelectedUserType(null);
            } else if (handleCommonApiErrors(errorMsg)) {
              // Error already handled by handleCommonApiErrors
              return;
            } else if (errorMsg) {
              setSnackbar({
                open: true,
                message: errorMsg,
                severity: "error",
              });
            } else {
              handleCloseDialog();
              setSnackbar({
                open: true,
                message: "Failed to add borrower",
                severity: "error",
              });
            }
          }
        } catch (error) {
          console.error("Error creating borrower:", error);

          const errorMsg = error?.message || error?.responseMessage || "";

          if (handleCommonApiErrors(errorMsg, true)) {
            return;
          } else {
            handleCloseDialog();
            setSnackbar({
              open: true,
              message: errorMsg || "Failed to create borrower",
              severity: "error",
            });
          }
        }
      } else if (userType === "consultant") {
        const response = await submitConsultant(data, "Consultant added successfully!");
        if (response) setSelectedUserType(null);
      } else {
        const response = await createUser(data);
        if (response && response.responseCode === 200) {
          handleCloseDialog();
          setSnackbar({
            open: true,
            message: response.responseMessage || "User added successfully!",
            severity: "success",
          });
          await refreshUsersList();
          setSelectedUserType(null);
        } else {
          setSnackbar({
            open: true,
            message: response?.responseMessage || "Failed to add user",
            severity: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error in handleAddUser:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to add user",
        severity: "error",
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const getBorrowerType = (userType, entityType) => {
    if (userType === "entity") {
      if (entityType === "PARTNERSHIP") {
        return "partnership_firm";
      } else if (entityType === "PROPRIETORSHIP") {
        return "propreitorship_firm";
      } else {
        return "company";
      }
    }
    return null;
  };

  const handleVerificationSuccess = async (message) => {
    setSnackbar({
      open: true,
      message: message || "OTP verified successfully",
      severity: "success",
    });

    setShowOTPVerification(false);

    const user = JSON.parse(localStorage.getItem("pendingLoanApplication"));
    if (!user) {
      setSnackbar({
        open: true,
        message: "Loan application data not found. Please try again.",
        severity: "error",
      });
      return;
    }

    const entityType = isEntityUser(user) ? "entity" : "individual";
    const businessType =
      entityType === "entity" ? determineBusinessType(user) : "";

    localStorage.setItem("loanApplicationUser", JSON.stringify(user));
    localStorage.setItem("userEntityType", entityType);
    localStorage.setItem("userBusinessType", businessType);

    navigate("/apply-loan?source=borrower&new=true");
  };

  const isEntityUser = (user) => {
    return (
      ["ENTITY", "entity"].includes(user.rawLendingType) ||
      user.userType === "Entity" ||
      user.userType?.toLowerCase() === "entity" ||
      user.userType?.startsWith("Entity - ") ||
      (user.borrowerType && user.borrowerType !== "individual")
    );
  };

  const determineBusinessType = (user) => {
    if (
      user.rawEntityType === "COMPANY" ||
      user.userType?.includes("Company") ||
      user.borrowerType === "company"
    ) {
      return "company";
    } else if (
      user.rawEntityType === "PARTNERSHIP" ||
      user.userType?.includes("Partnership") ||
      user.borrowerType?.includes("partnership")
    ) {
      return "partnership";
    } else if (
      user.rawEntityType === "PROPRIETORSHIP" ||
      user.userType?.includes("Proprietorship") ||
      user.borrowerType?.includes("proprietorship")
    ) {
      return "proprietorship";
    }
    return "company";
  };

  const handleVerificationError = (message) => {
    setSnackbar({
      open: true,
      message: message,
      severity: "error",
    });
  };

  const handleEditUser = async (data) => {
    try {
      isLoadingRef.current = true;
      setLoading(true);
      const updatedUser = {
        ...data,
        ip: IpAddress,
        longitude: userCoordinates?.lng || "",
        latitude: userCoordinates?.lat || "",
      };
      // Handle lender updates
      if (userType === "lender") {
        const lenderData = {
          id: selectedUser.uuid,
          financialInstitutionType: (
            data?.financialInstitutionType || ""
          ).toLowerCase(),
          financialInstitution: data?.financialInstitution || "",
          typeOfLending: (data?.typeOfLending || "").toLowerCase(),
          typeOfRetailLoans: (data?.typeOfRetailLoans || "").toLowerCase(),
          designation: data?.designation || "",
          userRequest: {
            mobileNumber: data?.phoneNumber || "",
            email: data?.email || "",
            name: data?.name || "",
          },
          location: data?.location || "",
        };

        try {
          const response = await updateLender(lenderData);
          if (!response || response.statusCode !== 200) {
            throw new Error(response?.message || "Failed to update lender");
          }

          await refreshUsersList();

          setSnackbar({
            open: true,
            message: "Lender updated successfully",
            severity: "success",
          });
          handleCloseDialog();
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.message || "Failed to update lender",
            severity: "error",
          });
        } finally {
          setLoading(false);
          isLoadingRef.current = false;
        }
      } else if (
        userType === "borrower" &&
        !selectedUser?.uuid?.startsWith("temp-")
      ) {
        // For borrowers, use userType for both API and display (formatted)
        updatedUser.userType = data?.userType || "individual";
        updatedUser.borrowerType = getBorrowerType(
          data?.userType,
          data?.entityType
        );
        updatedUser.entityName = data?.entityName || "";
        updatedUser.file = data.file;

        // Remove lendingType as borrowers use userType
        delete updatedUser?.lendingType;
        const response = await updateBorrower(updatedUser);
        if (
          response &&
          response.responseCode === 200 &&
          !response.responseMessage
            ?.toLowerCase()
            .includes("phone number is already used") &&
          !response.responseMessage
            ?.toLowerCase()
            .includes("email is already used")
        ) {
          setSnackbar({
            open: true,
            message: "Borrower updated successfully",
            severity: "success",
          });
          handleCloseDialog();
          await refreshUsersList(); // Refresh the list
        } else {
          setSnackbar({
            open: true,
            message: response?.responseMessage || "Failed to update borrower",
            severity: "error",
          });
        }
      } else if (userType === "consultant") {
        await submitConsultant(data, "Consultant updated successfully!");
        isLoadingRef.current = false;
        setLoading(false);
        return;
      } else if (!isBorrower(selectedUser)) {
        // For regular users (managers), use the update API
        const managerData = {
          uuid: selectedUser.uuid,
          name: data.name,
          phoneNumber: data.phoneNumber,
          email: data.email,
          username: data.username,
          roleId: data.roleId,
          region: data.region || "",
          area: data.area || "",
          ip: IpAddress,
          longitude: userCoordinates?.lng || "",
          latitude: userCoordinates?.lat || "",
          lendingType: data.userType || null,
          financierType: data.financierType || null,
        };
        // Make the API call
        const response = await updateUser(managerData);

        if (
          response &&
          (response.responseCode === 1 ||
            (response.responseCode === 200 &&
              response.responseMessage &&
              (response.responseMessage
                .toLowerCase()
                .includes("successfully") ||
                response.responseMessage === "User Updated!")))
        ) {
          // Reset loading state
          isLoadingRef.current = false;
          setLoading(false);

          handleCloseDialog();

          await refreshUsersList();

          // Show success message after data is loaded
          setSnackbar({
            open: true,
            message: "User updated successfully!",
            severity: "success",
          });

          return;
        } else {
          const errorMsg = response?.responseMessage || "Failed to update user";

          if (response?.responseCode === 200) {
            if (response.responseMessage === "User Updated!") {
              isLoadingRef.current = false;
              setLoading(false);

              handleCloseDialog();

              await refreshUsersList();

              setSnackbar({
                open: true,
                message: "User updated successfully!",
                severity: "success",
              });

              return;
            }

            if (
              response.responseMessage === "Username is already taken." ||
              response.responseMessage ===
                "Email is already used for another account." ||
              response.responseMessage ===
                "Phone number is already used for another account"
            ) {
              handleCommonApiErrors(response.responseMessage, true);

              setLoading(false);
              isLoadingRef.current = false;
              return;
            }
          }

          if (handleCommonApiErrors(errorMsg, true)) {
            setLoading(false);
            isLoadingRef.current = false;
            return;
          } else {
            handleCloseDialog();
            throw new Error(errorMsg);
          }
        }
      } else {
        // For temporary borrowers, just update in the state
        const updatedUsers = users.map((user) =>
          user.id === selectedUser.id ? { ...user, ...updatedUser } : user
        );

        setUsers(updatedUsers);
        handleCloseDialog();
        setSnackbar({
          open: true,
          message: "User updated successfully!",
          severity: "success",
        });
        setLoading(false);
        isLoadingRef.current = false;
      }
    } catch (error) {
      console.error("Error in handleEditUser:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to update user",
        severity: "error",
      });
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  const handleDeleteUser = useCallback(
    async (user) => {
      if (isLoadingRef.current || loading) return;

      try {
        isLoadingRef.current = true;
        setLoading(true);

        // For temporarily added borrowers (with temp- prefix in UUID), just remove from state
        if (user.uuid.startsWith("temp-")) {
          setUsers((prevUsers) => prevUsers.filter((u) => u.uuid !== user.uuid));

          setSnackbar({
            open: true,
            message: "Borrower deleted successfully!",
            severity: "success",
          });

          setLoading(false);
          isLoadingRef.current = false;
          return;
        }

        if (userType === "lender") {
          await deleteLender(user.id);
        } else if (userType === "consultant") {
          await deleteConsultantById(user.id);
        } else if (userType === "manager") {
          await deleteUser(user.uuid);
        } else if (userType === "borrower" || isBorrower(user)) {
          // For borrowers, use the borrower-specific delete API
          await deleteBorrower(user.uuid);
        } else {
          // For regular users (managers), use the general user delete API
          await deleteUser(user.uuid);
        }

        await refreshUsersList();

        setSnackbar({
          open: true,
          message: `${
            userType === "lender"
              ? "Lender"
              : userType === "borrower"
              ? "Borrower"
              : "User"
          } deleted successfully!`,
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting user:", error);
        setSnackbar({
          open: true,
          message:
            error.message ||
            `Failed to delete ${
              userType === "lender"
                ? "lender"
                : userType === "borrower"
                ? "borrower"
                : "user"
            }`,
          severity: "error",
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [loading, userType, deleteLender, deleteBorrower, deleteUser, refreshUsersList, setSnackbar, setUsers, isBorrower]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = useCallback(
    (status) => {
      switch (status) {
        case "Active":
          return theme.palette.success.main;
        case "Inactive":
          return theme.palette.error.main;
        default:
          return theme.palette.grey[500];
      }
    },
    [theme]
  );

  const getRoleColor = useCallback(
    (role) => {
      switch (role) {
        case "ADMIN":
          return theme.palette.primary.main;
        case "MANAGER":
          return theme.palette.secondary.main;
        case "USER":
          return theme.palette.info.main;
        default:
          return theme.palette.grey[500];
      }
    },
    [theme]
  );

  const getFinancierTypeColor = useCallback((type) => {
    switch (type) {
      case "Bank":
        return theme.palette.primary.dark;
      case "NBFC":
        return theme.palette.secondary.dark;
      case "Credit Union":
        return theme.palette.success.dark;
      case "Private Lender":
        return theme.palette.warning.dark;
      case "Government":
        return theme.palette.info.dark;
      default:
        return theme.palette.grey[500];
    }
  }, [theme]);

  const getVerificationStatusColor = useCallback((status) => {
    switch (status) {
      case "Verified":
        return theme.palette.success.main;
      case "Pending":
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  }, [theme]);

  const getTypeOfLendingColor = useCallback((type) => {
    switch (type?.toLowerCase()) {
      case "secured":
        return theme.palette.success.main;
      case "unsecured":
        return theme.palette.warning.main;
      case "both":
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  }, [theme]);

  

  const handleApplyLoan = useCallback(
    (user) => {
      if (!user) return;

      let borrowerType = "individual";
      let entityType = "";
      let businessType = "";

      if (user.borrowerType === "entity" || user.userType === "entity") {
        borrowerType = "entity";

        switch (
          user.entityType?.toLowerCase() ||
          user.borrowerType?.toLowerCase()
        ) {
          case "partnership_firm":
          case "partnership":
            entityType = "partnership_firm";
            businessType = "partnership";
            break;
          case "propreitorship_firm":
          case "proprietorship":
            entityType = "propreitorship_firm";
            businessType = "proprietorship";
            break;
          case "company":
            entityType = "company";
            businessType = "company";
            break;
          default:
            entityType = "company";
            businessType = "company";
        }
      }

      const userToPass = {
        ...user,
        id: user.id,
        uuid: user.uuid,
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber || user.mobileNumber,
        address: user.address,
        gstNumber: user.gstNumber,
        entityName: user.entityName,
        userType: borrowerType,
        borrowerType: entityType || borrowerType,
        entityType: entityType,
      };

      localStorage.setItem("userEntityType", borrowerType);
      localStorage.setItem("userBusinessType", businessType);
      localStorage.setItem("loanApplicationUser", JSON.stringify(userToPass));

      navigate("/apply-loan?source=borrower&new=true");
    },
    [navigate]
  );

  const handleViewUser = useCallback(
    (user) => {
      if (!user) {
        setSnackbar({ open: true, message: `Unable to view ${userType} details.`, severity: "error" });
        return;
      }

      if (userType === "consultant") {
        if (user.id) {
          navigate(`/users/${userType}s/${user.id}`);
        } else {
          setSnackbar({ open: true, message: "Consultant ID is missing.", severity: "error" });
        }
        return;
      }

      if (user.uuid) {
        navigate(`/users/${userType}s/${user.uuid}`);
      } else {
        setSnackbar({
          open: true,
          message: `Unable to view ${userType} details. User UUID is missing.`,
          severity: "error",
        });
      }
    },
    [navigate, userType, setSnackbar]
  );

  const handleViewProposals = useCallback(
    (lender) => {
      if (!lender.uuid) {
        setSnackbar({
          open: true,
          message: "Cannot view proposals: Lender UUID is missing",
          severity: "error",
        });
        return;
      }
      navigate(`/proposals/lender/${lender.uuid}`);
    },
    [navigate, setSnackbar]
  );

  const handleOpenVerifyDialog = useCallback((lender) => {
    setSelectedLender(lender);
    setVerifyDialogOpen(true);
  }, []);

  const renderCellContent = useCallback(
    (column, user) => {
      if (column.id === "userType") {
        // For the userType column, use the pre-formatted displayType
        const displayType =
          user.displayType ||
          formatBorrowerType(
            user.borrowerType,
            user.entityType,
            user.entityName
          );

        // Determine chip color based on borrower type and entity type
        let chipColor;
        if (user.borrowerType === "individual") {
          chipColor = theme.palette.primary.main;
        } else if (user.borrowerType === "entity") {
          switch (user.entityType) {
            case "company":
              chipColor = theme.palette.secondary.main;
              break;
            case "partnership_firm":
              chipColor = theme.palette.success.main;
              break;
            case "propreitorship_firm":
              chipColor = theme.palette.warning.main;
              break;
            default:
              chipColor = theme.palette.info.main;
          }
        } else {
          chipColor = theme.palette.info.main;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label={displayType}
              size="small"
              sx={{
                bgcolor: alpha(chipColor, 0.1),
                color: chipColor,
                fontWeight: 500,
                height: "auto",
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  lineHeight: "1.2",
                  padding: "8px 12px",
                },
              }}
            />
          </Box>
        );
      }

      const value = user[column.id];

      if (column.id === "actions") {
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => handleViewUser(user)}
                sx={{
                  color: theme.palette.info.main,
                  "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.1) },
                }}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>

            {userType === "lender" && (
              <>
                <Tooltip title="View Proposals">
                  <IconButton
                    size="small"
                    onClick={() => handleViewProposals(user)}
                    sx={{
                      color: theme.palette.primary.main,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <ProposalIcon />
                  </IconButton>
                </Tooltip>
                {!user.verified && (
                  <Tooltip title="Verify Lender">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenVerifyDialog(user)}
                      sx={{
                        color: theme.palette.success.main,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                        },
                      }}
                    >
                      <VerifiedUserIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {userType === "borrower" && isBorrower(user) && (
              <Tooltip title="Apply Loan">
                <IconButton
                  size="small"
                  onClick={() => handleApplyLoan(user)}
                  sx={{
                    color: theme.palette.success.main,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                    },
                  }}
                >
                  <MonetizationOnIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Common actions for all user types */}
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(user)}
                sx={{
                  color: theme.palette.warning.main,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteUser(user)}
                sx={{
                  color: theme.palette.error.main,
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.1) },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      // Handle lender-specific fields
      if (userType === "lender") {
        switch (column.id) {
          case "name":
            return (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {value || "N/A"}
              </Typography>
            );
          case "uuid":
            return (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {value || "N/A"}
              </Typography>
            );
          case "financierType":
            return (
              <Chip
                label={value}
                size="small"
                sx={{
                  bgcolor: alpha(getFinancierTypeColor(value), 0.1),
                  color: getFinancierTypeColor(value),
                  fontWeight: 500,
                }}
              />
            );
          case "typeOfLending":
            return (
              <Chip
                label={value}
                size="small"
                sx={{
                  bgcolor: alpha(getTypeOfLendingColor(value), 0.1),
                  color: getTypeOfLendingColor(value),
                  fontWeight: 500,
                }}
              />
            );
          case "typeOfRetailLoans":
            return (
              <Typography variant="body2" color="text.secondary">
                {value || "N/A"}
              </Typography>
            );
          case "designation":
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "capitalize" }}
              >
                {value || "N/A"}
              </Typography>
            );
          case "verificationStatus":
            return (
              <Chip
                icon={<VerifiedUserIcon />}
                label={value}
                size="small"
                sx={{
                  bgcolor: alpha(getVerificationStatusColor(value), 0.1),
                  color: getVerificationStatusColor(value),
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: "inherit",
                  },
                }}
              />
            );
          case "taggedLocation":
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationIcon
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
                <Typography variant="body2" color="text.secondary">
                  {value || "N/A"}
                </Typography>
              </Box>
            );
          case "region":
            return user.region || "N/A";
          case "area":
            return user.area || "N/A";
          default:
            return value || "N/A";
        }
      }

      // Handle other user types (managers and borrowers)
      switch (column.id) {
        case "name":
        case "email":
        case "username":
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: column.id === "name" ? 500 : 400 }}
            >
              {value || "N/A"}
            </Typography>
          );
        case "professionalWebsite": {
          const url = (value || "").trim();
          return url ? (
            <Typography variant="body2" sx={{ maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <a href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer">
                {url}
              </a>
            </Typography>
          ) : (
            "N/A"
          );
        }
        case "loanSpecialization": {
          const items = Array.isArray(value) ? value : (value ? [value] : []);
          if (!items.length) return "N/A";

          const visibleLimit = 3;
          const visibleItems = items.slice(0, visibleLimit);
          const hiddenItems = items.slice(visibleLimit);

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: 0.5,
                rowGap: 0.5,
                alignItems: "center",
              }}
            >
              {visibleItems.map((item, idx) => (
                <Chip
                  key={`${item}-${idx}`}
                  label={item}
                  size="small"
                  sx={{ typography: "caption" }}
                />
              ))}
              {hiddenItems.length > 0 && (
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <Tooltip
                    title={
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {hiddenItems.map((item, idx) => (
                          <Chip
                            key={`hidden-${item}-${idx}`}
                            label={item}
                            size="small"
                            sx={{ typography: "caption" }}
                          />
                        ))}
                      </Box>
                    }
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor:
                            theme.palette.mode === "light"
                              ? theme.palette.common.white
                              : theme.palette.grey[800],
                          color: theme.palette.text.primary,
                          boxShadow: 3,
                          borderRadius: 2,
                          p: 1,
                          maxWidth: 260,
                        },
                      },
                    }}
                  >
                    <Chip
                      label={`+${hiddenItems.length}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        typography: "caption",
                        cursor: "default",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        }
        case "region":
        case "area":
          return (
            <Typography
              variant="body2"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {value || "N/A"}
            </Typography>
          );
        case "role":
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                bgcolor: alpha(getRoleColor(value), 0.1),
                color: getRoleColor(value),
                fontWeight: 500,
              }}
            />
          );
        case "status":
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                bgcolor: alpha(getStatusColor(value), 0.1),
                color: getStatusColor(value),
                fontWeight: 500,
              }}
            />
          );
        case "phoneNumber":
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
              <Typography variant="body2" color="text.secondary">
                {value || "N/A"}
              </Typography>
            </Box>
          );
        case "taggedLocation":
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationIcon
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
              <Typography variant="body2" color="text.secondary">
                {value || "N/A"}
              </Typography>
            </Box>
          );
        default:
          return value || "N/A";
      }
    },
    [
      theme,
      userType,
      handleViewUser,
      handleViewProposals,
      handleOpenVerifyDialog,
      handleApplyLoan,
      handleOpenDialog,
      handleDeleteUser,
      formatBorrowerType,
      getFinancierTypeColor,
      getTypeOfLendingColor,
      getVerificationStatusColor,
      getRoleColor,
      getStatusColor,
      isBorrower,
    ]
  );
  

  // Function to handle loan application submission
  const handleLoanSubmit = (event) => {
    event.preventDefault();

    // Show success message
    setSnackbar({
      open: true,
      message: `Loan application for ${selectedBorrower.name} submitted successfully!`,
      severity: "success",
    });

    // Close the dialog
    setLoanDialogOpen(false);
    setSelectedBorrower(null);
  };

  // Loan Application Dialog Component
  const LoanApplicationDialog = () => {
    return (
      <Dialog
        open={loanDialogOpen}
        onClose={() => setLoanDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2.5,
            background: alpha(theme.palette.primary.main, 0.06),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <MonetizationOnIcon
              sx={{
                color: theme.palette.primary.main,
                mr: 1.5,
                fontSize: 28,
              }}
            />
            <Box>
              <Typography variant="h6">Loan Application</Typography>
              {selectedBorrower && (
                <Typography variant="caption" color="text.secondary">
                  Borrower: {selectedBorrower.name}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setLoanDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleLoanSubmit}>
          <DialogContent sx={{ px: 3, py: 2 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Loan Type</InputLabel>
                  <Select name="loanType" defaultValue="" label="Loan Type">
                    <MenuItem value="PERSONAL">Personal Loan</MenuItem>
                    <MenuItem value="BUSINESS">Business Loan</MenuItem>
                    <MenuItem value="HOME">Home Loan</MenuItem>
                    <MenuItem value="VEHICLE">Vehicle Loan</MenuItem>
                    <MenuItem value="EDUCATION">Education Loan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Amount"
                  name="amount"
                  type="number"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: "₹",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tenure (months)"
                  name="tenure"
                  type="number"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Purpose"
                  name="purpose"
                  multiline
                  rows={3}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button onClick={() => setLoanDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Submit Application
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  

  const handleCloseVerifyDialog = () => {
    setVerifyDialogOpen(false);
    setVerifyUserId("");
    setVerifyLenderId("");
    setSelectedLender(null);
  };

  const handleVerifyLenderIdChange = (e) => {
    const newLenderId = e.target.value;
    setVerifyLenderId(newLenderId);
  };

  const handleVerifyUserIdChange = (e) => {
    const newUserId = e.target.value;
    setVerifyUserId(newUserId);
  };

  const handleVerifyLender = async () => {
    if (!verifyLenderId || verifyLoading) return;

    try {
      setVerifyLoading(true);

      const lenderDetails = await getLenderById(verifyLenderId.toString());

      if (!lenderDetails?.result?.lender?.id) {
        throw new Error("Could not find user ID for this lender");
      }

      const userId = lenderDetails.result.lender.id;

      const response = await verifyLender(
        parseInt(userId, 10),
        verifyLenderId.toString()
      );

      if (response && response.statusCode === 200) {
        // Update the local state to reflect the change
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === verifyLenderId
              ? { ...user, verified: true, status: "Active" }
              : user
          )
        );

        setSnackbar({
          open: true,
          message: response.message || "Lender verified successfully",
          severity: "success",
        });

        handleCloseVerifyDialog();

        // Refresh the lenders list
        await refreshUsersList();
      } else {
        throw new Error(response?.message || "Failed to verify lender");
      }
    } catch (error) {
      console.error("Error verifying lender:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to verify lender",
        severity: "error",
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleOpenLenderOnboarding = () => {
    setLenderOnboardingOpen(true);
  };

  const handleCloseLenderOnboarding = () => {
    setLenderOnboardingOpen(false);
    loadUsers();
  };

  // Create an object with cell renderers for each column
  const customCellRenderers = useMemo(() => {
    const renderers = {};
    columns.forEach((column) => {
      renderers[column.id] = (user) => renderCellContent(column, user);
    });
    return renderers;
  }, [columns, renderCellContent]);

  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;

    const searchLower = searchTerm.toLowerCase();

    return users.filter((user) => {
      // Common fields to search in all user types
      const commonFieldsMatch = [
        user.name,
        user.email,
        user.phoneNumber,
        user.mobileNumber, // Some records use mobileNumber instead of phoneNumber
        user.taggedLocation,
      ].some(
        (field) => field && field.toString().toLowerCase().includes(searchLower)
      );

      if (commonFieldsMatch) return true;

      // Manager-specific fields
      if (userType === "manager") {
        return [user.username, user.role, user.region, user.area].some(
          (field) =>
            field && field.toString().toLowerCase().includes(searchLower)
        );
      }

      // Lender-specific fields
      if (userType === "lender") {
        return [
          user.uuid,
          user.financierType,
          user.typeOfLending,
          user.typeOfRetailLoans,
          user.designation,
          user.verificationStatus,
        ].some(
          (field) =>
            field && field.toString().toLowerCase().includes(searchLower)
        );
      }

      // Borrower-specific fields
      if (userType === "borrower") {
        return [
          user.businessType,
          user.entityType,
          user.entityName,
          user.gstNumber,
        ].some(
          (field) =>
            field && field.toString().toLowerCase().includes(searchLower)
        );
      }

      // Consultant-specific fields
      if (userType === "consultant") {
        const listMatches = (arr) =>
          Array.isArray(arr) && arr.some((item) =>
            item && item.toString().toLowerCase().includes(searchLower)
          );
        return [
          user.companyName,
          user.professionalWebsite,
          user.yearsOfExperience,
        ].some((field) => field && field.toString().toLowerCase().includes(searchLower))
          || listMatches(user.loanSpecialization);
      }

      return false;
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight="600" color="primary">
          {pageTitle}
        </Typography>
        {permission.create === 1 &&
          (userType === "lender" ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenLenderOnboarding}
            >
              Add New Lender
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenTypeSelectDialog}
            >
              {userType === "borrower" ? "Add New Borrower" : userType === "consultant" ? "Add New Consultant" : "Add New Manager"}
            </Button>
          ))}
      </Box>

      {/* Search and Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
          background: (theme) => alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={`Search ${
                userType === "manager" || userType === "consultant"
                  ? `${userType === "consultant" ? "consultants" : "managers"} by name, email, username...`
                  : userType === "borrower"
                  ? "borrowers by name, email, type..."
                  : "lenders by name, institution..."
              }`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                initialLoadRef.current = false;
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchQuery("");
                        initialLoadRef.current = false;
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "text.primary",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.paper, 0.8),
                  "&:hover": {
                    bgcolor: "background.paper",
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: "1px",
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          overflow: "hidden",
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          background: (theme) => alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <DataTable
          permissions={JSON.parse(
            userPermission.find((e) => e.module === "leads")?.permission
          )}
          columns={columns}
          data={filterUsers(users, searchQuery)}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={
            userType === "manager" || userType === "consultant" || userType === "borrower" || userType === "lender"
              ? (totalCount ?? undefined)
              : undefined
          }
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          getStatusColor={getStatusColor}
          onViewClick={handleViewUser}
          onEditClick={handleOpenDialog}
          onDeleteClick={handleDeleteUser}
          onApplyLoanClick={handleApplyLoan}
          onViewProposalsClick={handleViewProposals}
          onVerifyClick={
            userType === "lender" ? handleOpenVerifyDialog : undefined
          }
          isBorrower={isBorrower}
          isLender={userType === "lender"}
          customCellRenderer={customCellRenderers}
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
          noDataMessage={`No ${
            userType === "manager" || userType === "consultant"
              ? userType === "consultant" ? "consultants" : "managers"
              : userType === "borrower"
              ? "borrowers"
              : "lenders"
          } found${searchQuery ? " matching your search." : "."}`}
        />
      </Paper>

      {/* User Form Dialog */}
      <UserForm
        open={open}
        handleClose={handleCloseDialog}
        onSubmit={mode === "add" ? handleAddUser : handleEditUser}
        user={selectedUser}
        mode={mode}
        userType={userType}
        error={error}
        setError={setError}
      />

      {/* Borrower Type Select Modal */}
      <BorrowerTypeSelectModal
        open={typeSelectOpen}
        handleClose={handleCloseTypeSelectDialog}
        onSelectBorrowerType={handleSelectUserType}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loan Application Dialog */}
      <LoanApplicationDialog />

      <OTPVerification
        showOTPVerification={showOTPVerification}
        setShowOTPVerification={setShowOTPVerification}
        showMobileOTP={showMobileOTP}
        requestData={borrowerRequestData}
        onVerificationSuccess={handleVerificationSuccess}
        onVerificationError={handleVerificationError}
      />

      {/* Verify Lender Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={handleCloseVerifyDialog}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
            background: (theme) =>
              `linear-gradient(145deg, ${alpha(
                theme.palette.background.paper,
                0.98
              )}, ${alpha(theme.palette.background.paper, 1)})`,
            backdropFilter: "blur(8px)",
            transition: "all 0.3s ease-in-out",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              color="text.primary"
              sx={{
                letterSpacing: "-0.3px",
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Verify Lender
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                opacity: 0.8,
              }}
            >
              Approve access for {selectedLender?.name}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseVerifyDialog}
            sx={{
              color: "text.secondary",
              width: 36,
              height: 36,
              borderRadius: "12px",
              transition: "all 0.2s ease",
              "&:hover": {
                background: (theme) =>
                  alpha(theme.palette.text.secondary, 0.08),
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
            fullWidth
            label="Lender ID"
            value={verifyLenderId}
            onChange={handleVerifyLenderIdChange}
            type="text"
            placeholder="Enter Lender ID"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleCloseVerifyDialog}
            sx={{
              borderRadius: "12px",
              px: 3,
              py: 1,
              color: "text.secondary",
              "&:hover": {
                background: (theme) =>
                  alpha(theme.palette.text.secondary, 0.08),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyLender}
            variant="contained"
            disabled={!verifyLenderId || verifyLoading}
            startIcon={<VerifiedUserIcon />}
            sx={{
              borderRadius: "12px",
              px: 3,
              py: 1,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: (theme) =>
                `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
              transition: "all 0.2s ease",
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: "translateY(-1px)",
                boxShadow: (theme) =>
                  `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&.Mui-disabled": {
                background: (theme) =>
                  alpha(theme.palette.action.disabled, 0.12),
              },
            }}
          >
            {verifyLoading ? "Verifying..." : "Approve Access"}
          </Button>
        </DialogActions>
      </Dialog>

      <LenderOnboardingPopup
        open={lenderOnboardingOpen}
        onClose={handleCloseLenderOnboarding}
      />
    </Box>
  );
};

export default UserManagement;
